import { useState, useEffect } from "react";
import { useSupabaseAuth } from "@/hooks/auth";
import {
  studentReportingService,
  StudentReportData,
  CourseAssignmentDetail,
  ReportFilters,
} from "@/services/studentReportingService";
import { reportExportUtils } from "@/utils/reportExportUtils";
import { ReportFilterPanel } from "./ReportFilterPanel";
import { AllStudentsTable } from "./AllStudentsTable";
import { IndividualStudentTable } from "./IndividualStudentTable";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

export const StudentProgressReport = () => {
  const { hasAccess } = useSupabaseAuth();
  const hasStaffAccess = hasAccess('staff');
  const { toast } = useToast();

  const [viewMode, setViewMode] = useState<'all' | 'individual'>('all');
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [filters, setFilters] = useState<ReportFilters>({});

  const [allStudentsData, setAllStudentsData] = useState<StudentReportData[]>([]);
  const [individualStudentData, setIndividualStudentData] = useState<CourseAssignmentDetail[]>([]);

  const [students, setStudents] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [courses, setCourses] = useState<Array<{ id: string; title: string }>>([]);

  const [loading, setLoading] = useState(false);

  // Load available students and courses for filters
  useEffect(() => {
    const loadFilterOptions = async () => {
      const [studentsData, coursesData] = await Promise.all([
        studentReportingService.fetchAvailableStudents(),
        studentReportingService.fetchAvailableCourses(),
      ]);
      setStudents(studentsData);
      setCourses(coursesData);
    };
    loadFilterOptions();
  }, []);

  // Load data based on view mode
  useEffect(() => {
    if (!hasStaffAccess) return;

    const loadData = async () => {
      setLoading(true);
      try {
        if (viewMode === 'all') {
          const data = await studentReportingService.fetchAllStudentsReport(filters);
          setAllStudentsData(data);
        } else if (viewMode === 'individual' && selectedStudentId) {
          const data = await studentReportingService.fetchIndividualStudentReport(
            selectedStudentId,
            filters
          );
          setIndividualStudentData(data);
        }
      } catch (error) {
        console.error('Error loading report data:', error);
        toast({
          title: "Error Loading Report",
          description: "Failed to load report data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [viewMode, selectedStudentId, filters, hasStaffAccess, toast]);

  const handleViewModeChange = (mode: 'all' | 'individual') => {
    setViewMode(mode);
    if (mode === 'all') {
      setSelectedStudentId(null);
    }
  };

  const handleStudentChange = (studentId: string | null) => {
    setSelectedStudentId(studentId);
  };

  const handleRowClick = (studentId: string) => {
    setSelectedStudentId(studentId);
    setViewMode('individual');
  };

  const handleExport = () => {
    try {
      if (viewMode === 'all') {
        reportExportUtils.exportAllStudentsToCSV(allStudentsData);
        toast({
          title: "Export Successful",
          description: "All students report has been downloaded.",
        });
      } else if (viewMode === 'individual') {
        reportExportUtils.exportIndividualStudentToCSV(individualStudentData);
        toast({
          title: "Export Successful",
          description: "Individual student report has been downloaded.",
        });
      }
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export report. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleClearFilters = () => {
    setFilters({});
    toast({
      title: "Filters Cleared",
      description: "All filters have been reset.",
    });
  };

  if (!hasStaffAccess) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">You don't have permission to view reports.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Student Progress & Assignments Report</h2>
        <p className="text-muted-foreground mt-2">
          View comprehensive student progress data and export reports
        </p>
      </div>

      <ReportFilterPanel
        viewMode={viewMode}
        selectedStudentId={selectedStudentId}
        filters={filters}
        students={students}
        courses={courses}
        onViewModeChange={handleViewModeChange}
        onStudentChange={handleStudentChange}
        onFiltersChange={setFilters}
        onExport={handleExport}
        onClearFilters={handleClearFilters}
      />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading report data...</span>
        </div>
      ) : viewMode === 'all' ? (
        <AllStudentsTable data={allStudentsData} onRowClick={handleRowClick} />
      ) : viewMode === 'individual' && selectedStudentId ? (
        <IndividualStudentTable data={individualStudentData} />
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          Please select a student to view their detailed report
        </div>
      )}
    </div>
  );
};
